import MuiButton from '@mui/material/Button';
import type { SxProps, Theme } from '@mui/material/styles';
import { alpha, styled } from '@mui/material/styles';

const smoothEasing = 'cubic-bezier(0.4, 0, 0.2, 1)';
const buttonWideUppercaseLineHeight = 1.125;
const buttonComfortableLineHeight = 1.2;
const buttonCompactUppercaseLineHeight = 1.15;
const buttonDialogLineHeight = 1.5;

export const buttonPrimarySx: SxProps<Theme> = {
  backgroundColor: 'primary.main',
  borderRadius: 1,
  boxShadow: 'none',
  color: 'primary.contrastText',
  fontSize: 16,
  fontStyle: 'normal',
  fontWeight: 600,
  minHeight: 50,
  letterSpacing: '0.08em',
  lineHeight: buttonWideUppercaseLineHeight,
  paddingBottom: '16px',
  paddingLeft: '32px',
  paddingRight: '32px',
  paddingTop: '16px',
  textTransform: 'uppercase',
  transition: (theme: Theme) =>
    theme.transitions.create(['background-color', 'box-shadow', 'filter'], {
      duration: 280,
      easing: theme.transitions.easing.easeInOut,
    }),
  '&:hover': {
    backgroundColor: 'primary.light',
    boxShadow: (theme: Theme) => `0 8px 28px ${alpha(theme.palette.primary.main, 0.48)}`,
    filter: 'none',
  },
  '&:active': {
    backgroundColor: 'primary.dark',
    boxShadow: (theme: Theme) => `0 4px 14px ${alpha(theme.palette.primary.main, 0.36)}`,
    filter: 'none',
  },
  '&[data-loading="true"].Mui-disabled': {
    backgroundColor: 'action.disabledBackground',
    color: 'action.disabled',
    opacity: 1,
  },
};

export const buttonPositiveSx: SxProps<Theme> = {
  ...buttonPrimarySx,
  backgroundColor: 'success.main',
  color: 'success.contrastText',
  '&:hover': {
    backgroundColor: 'success.light',
    boxShadow: (theme: Theme) => `0 8px 28px ${alpha(theme.palette.success.main, 0.48)}`,
    filter: 'none',
  },
  '&:active': {
    backgroundColor: 'success.dark',
    boxShadow: (theme: Theme) => `0 4px 14px ${alpha(theme.palette.success.main, 0.36)}`,
    filter: 'none',
  },
  '&[data-loading="true"].Mui-disabled': {
    backgroundColor: 'action.disabledBackground',
    color: 'action.disabled',
    opacity: 1,
  },
};

export const buttonNeutralOutlineSx: SxProps<Theme> = {
  backgroundColor: 'background.paper',
  borderColor: 'divider',
  borderRadius: 1,
  borderStyle: 'solid',
  borderWidth: 1,
  color: 'neutral.black',
  fontSize: 16,
  fontStyle: 'normal',
  fontWeight: 400,
  letterSpacing: 0,
  lineHeight: buttonComfortableLineHeight,
  paddingBottom: '17px',
  paddingLeft: (theme: Theme) => theme.spacing(1.5),
  paddingRight: (theme: Theme) => theme.spacing(1.5),
  paddingTop: '17px',
  textTransform: 'none',
  transition: (theme: Theme) =>
    theme.transitions.create(['background-color', 'border-color'], {
      duration: 280,
      easing: theme.transitions.easing.easeInOut,
    }),
  '&:hover': {
    backgroundColor: 'background.default',
    borderColor: 'text.disabled',
  },
  '&:active': {
    backgroundColor: 'action.hover',
    borderColor: 'text.disabled',
  },
  '& .MuiButton-startIcon': {
    marginLeft: 0,
    marginRight: '8px',
  },
  '& .MuiButton-startIcon > *:first-of-type': {
    display: 'block',
    height: 24,
    width: 24,
  },
};

export const buttonPrimaryOutlineSx: SxProps<Theme> = {
  backgroundColor: 'background.paper',
  borderColor: 'primary.main',
  borderRadius: 1,
  borderStyle: 'solid',
  borderWidth: 1,
  color: 'primary.main',
  fontSize: 16,
  fontStyle: 'normal',
  fontWeight: 600,
  letterSpacing: '0.08em',
  lineHeight: buttonWideUppercaseLineHeight,
  minHeight: 50,
  paddingBottom: '16px',
  paddingLeft: '32px',
  paddingRight: '32px',
  paddingTop: '16px',
  textTransform: 'uppercase',
  transition: (theme: Theme) =>
    theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
      duration: 280,
      easing: theme.transitions.easing.easeInOut,
    }),
  '&:hover': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
    borderColor: 'primary.main',
    boxShadow: 'none',
    color: 'primary.main',
  },
  '&:active': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.14),
    borderColor: 'primary.main',
    boxShadow: 'none',
    color: 'primary.main',
  },
};

export const buttonBrandSx: SxProps<Theme> = {
  backgroundColor: 'brand.500',
  borderColor: 'brand.500',
  borderRadius: 1,
  borderStyle: 'solid',
  borderWidth: 1,
  boxShadow: 'none',
  color: 'common.white',
  fontSize: 14,
  fontStyle: 'normal',
  fontWeight: 600,
  letterSpacing: 0,
  lineHeight: buttonDialogLineHeight,
  minHeight: 32,
  minWidth: 0,
  padding: '6px 12px',
  textTransform: 'none',
  transition: (theme: Theme) =>
    theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'filter'], {
      duration: 220,
      easing: theme.transitions.easing.easeInOut,
    }),
  '&:hover': {
    backgroundColor: 'brand.600',
    borderColor: 'brand.600',
    boxShadow: 'none',
    color: 'common.white',
    filter: 'none',
  },
  '&:active': {
    backgroundColor: 'brand.700',
    borderColor: 'brand.700',
    boxShadow: 'none',
    color: 'common.white',
    filter: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: 'neutral.50',
    borderColor: 'neutral.200',
    color: 'neutral.400',
    opacity: 1,
  },
  '&[data-loading="true"].Mui-disabled': {
    backgroundColor: 'brand.500',
    borderColor: 'brand.500',
    color: 'common.white',
    opacity: 0.72,
  },
};

export const buttonBrandOutlineSx: SxProps<Theme> = {
  backgroundColor: 'background.paper',
  borderColor: 'brand.500',
  borderRadius: 1,
  borderStyle: 'solid',
  borderWidth: 1,
  boxShadow: 'none',
  color: 'brand.500',
  fontSize: 14,
  fontStyle: 'normal',
  fontWeight: 600,
  letterSpacing: 0,
  lineHeight: buttonDialogLineHeight,
  minHeight: 32,
  minWidth: 0,
  padding: '6px 12px',
  textTransform: 'none',
  transition: (theme: Theme) =>
    theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'color'], {
      duration: 220,
      easing: theme.transitions.easing.easeInOut,
    }),
  '&:hover': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.brand[500], 0.06),
    borderColor: 'brand.500',
    boxShadow: 'none',
    color: 'brand.500',
    filter: 'none',
  },
  '&:active': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.brand[500], 0.1),
    borderColor: 'brand.500',
    boxShadow: 'none',
    color: 'brand.500',
    filter: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: 'neutral.50',
    borderColor: 'neutral.200',
    color: 'neutral.400',
    opacity: 1,
  },
};

export const buttonDangerSx: SxProps<Theme> = {
  backgroundColor: 'red.600',
  borderColor: 'red.600',
  borderRadius: 1,
  borderStyle: 'solid',
  borderWidth: 1,
  boxShadow: 'none',
  color: 'common.white',
  fontSize: 14,
  fontStyle: 'normal',
  fontWeight: 600,
  letterSpacing: 0,
  lineHeight: buttonDialogLineHeight,
  minHeight: 32,
  minWidth: 0,
  padding: '6px 12px',
  textTransform: 'none',
  transition: (theme: Theme) =>
    theme.transitions.create(['background-color', 'border-color', 'box-shadow', 'filter'], {
      duration: 220,
      easing: theme.transitions.easing.easeInOut,
    }),
  '&:hover': {
    backgroundColor: 'red.700',
    borderColor: 'red.700',
    boxShadow: 'none',
    color: 'common.white',
    filter: 'none',
  },
  '&:active': {
    backgroundColor: 'red.800',
    borderColor: 'red.800',
    boxShadow: 'none',
    color: 'common.white',
    filter: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: 'neutral.50',
    borderColor: 'neutral.200',
    color: 'neutral.400',
    opacity: 1,
  },
  '&[data-loading="true"].Mui-disabled': {
    backgroundColor: 'red.600',
    borderColor: 'red.600',
    color: 'common.white',
    opacity: 0.72,
  },
};

export const buttonPrimaryGhostSx: SxProps<Theme> = {
  backgroundColor: 'transparent',
  borderRadius: 1,
  boxShadow: 'none',
  color: 'primary.main',
  filter: 'none',
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: buttonCompactUppercaseLineHeight,
  minHeight: 40,
  paddingBottom: '12px',
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingTop: '12px',
  textTransform: 'uppercase',
  transition: (theme: Theme) =>
    theme.transitions.create(['background-color', 'box-shadow', 'filter'], {
      duration: 220,
      easing: theme.transitions.easing.easeInOut,
    }),
  '&:hover': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.06),
    boxShadow: 'none',
    filter: 'none',
  },
  '&:active': {
    backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.1),
    boxShadow: 'none',
    filter: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    color: 'action.disabled',
    filter: 'none',
    opacity: 1,
  },
};

export const buttonCompactSx: SxProps<Theme> = {
  borderRadius: 1,
  boxShadow: 'none',
  filter: 'none',
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: buttonCompactUppercaseLineHeight,
  minHeight: 40,
  paddingBottom: '12px',
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingTop: '12px',
  textTransform: 'uppercase',
  '&.MuiButton-contained, &.MuiButton-outlined, &.MuiButton-text': {
    boxShadow: 'none',
    filter: 'none',
  },
  '&.MuiButton-contained:not(.Mui-disabled), &.MuiButton-outlined:not(.Mui-disabled), &.MuiButton-text:not(.Mui-disabled)': {
    boxShadow: 'none',
    filter: 'none',
    '&:hover, &:active': {
      boxShadow: 'none',
      filter: 'none',
    },
  },
  '&.Mui-disabled': {
    boxShadow: 'none',
    filter: 'none',
    opacity: 1,
  },
};

export const StyledButton = styled(MuiButton)(({theme}) => ({
  minHeight: 48,
  borderRadius: 14,
  textTransform: 'none',
  fontWeight: 700,
  boxShadow: 'none',
  transition: theme.transitions.create(['background-color', 'color', 'border-color', 'box-shadow', 'filter'], {
    duration: 280,
    easing: smoothEasing,
  }),
  '&:hover': {
    boxShadow: 'none',
  },
  '&.MuiButton-contained:not(.Mui-disabled)': {
    transition: theme.transitions.create(['background-color', 'color', 'box-shadow', 'filter'], {
      duration: 320,
      easing: smoothEasing,
    }),
    '&:hover': {
      boxShadow: `0 6px 18px ${alpha(theme.palette.common.black, 0.14)}`,
      filter: 'brightness(0.91)',
    },
    '&:active': {
      boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.12)}`,
      filter: 'brightness(0.86)',
      transitionDuration: '140ms',
    },
  },
  '&.MuiButton-outlined:not(.Mui-disabled)': {
    transition: theme.transitions.create(['background-color', 'color', 'border-color', 'box-shadow'], {
      duration: 280,
      easing: smoothEasing,
    }),
    '&:hover, &:active': {
      filter: 'none',
    },
    '&:active': {
      transitionDuration: '140ms',
    },
  },
  '&.Mui-disabled': {
    filter: 'none',
    opacity: 0.7,
  },
  '&:focus-visible': {
    outline: `3px solid ${alpha(theme.palette.primary.main, 0.28)}`,
    outlineOffset: 2,
  },
})) as typeof MuiButton;
